import { addMonths, differenceInMonths, differenceInYears, setDate, setMonth, addYears } from "date-fns";
import type { Config } from "~/shared/api/useConfig";
import type { HoldingsDto } from "~/shared/api/useHoldings";
import type { F2100PolicyDetailsDto } from "~/shared/api/usePolicyDetails";
import type { CustomerState } from "~/shared/reducers/customer";
import type { PolicyView } from "~/shared/reducers/policies";
import { NJORD_UTBETALING_PRODUCTS } from "~/utils/policies";

export const UTBETALING_BESTILT = "UTBETALING_BESTILT";
export const UNDER_UTBETALING = "UNDER_UTBETALING";
export const AKTIV_UNDER_UTBETALING = "AKTIV_UNDER_UTBETALING";
export const UTBETALING_AVSLUTTET = "UTBETALING_AVSLUTTET";

type CanOrderUtbetalingPolicy = PolicyView | F2100PolicyDetailsDto;

export const canOrderUtbetaling = (dateOfBirth: number | Date, policy: CanOrderUtbetalingPolicy, holdings?: HoldingsDto) => {
	const epkDetails = policy.epkDetaljer;
	const policyKonfig = policy.konfig;
	const ageForUtbetaling = epkDetails?.blEpk?.lavesteUtbetalingsalder || policyKonfig.ageForUtbetaling;
	const sixMonthsAhead = addMonths(setDate(new Date(), 1), 6);
	const balance = ("markedsverdi" in policy ? policy.markedsverdi : 0) || holdings?.currentMarketValue || 0;
	return balance > 0 && differenceInYears(sixMonthsAhead, setDate(dateOfBirth, 1)) >= ageForUtbetaling;
};

export const canOrderNjordUtbetaling = (age, policy) => NJORD_UTBETALING_PRODUCTS.includes(policy.productId) && age >= 62;

export const calculateRemainingUtbetalingDuration = (_endDate) => {
	const today = new Date();
	today.setHours(8);

	const endDate = new Date(_endDate);
	endDate.setHours(20);

	let months = (differenceInMonths(endDate, today) % 12) + 1;
	let years = differenceInYears(endDate, today);
	if (months === 12) {
		years += 1;
		months = 0;
	}

	return {
		disbursementDurationMonths: months,
		disbursementDurationYears: years,
	};
};

export const roundMonthlyUtbetaling = (utbetaling: number) => {
	if (utbetaling < 100) {
		return Math.round(utbetaling / 10) * 10;
	}
	return Math.round(utbetaling / 100) * 100;
};

export const showDisbursementInfo = (policy) =>
	!policy.utbetaling.utbetalingStatus && (policy.utbetaling.livsvarigUtbetaling || (!!policy.utbetaling.utbetalingStart && !!policy.utbetaling.utbetalingSlutt));

export const showStartUtbetaling = (customer: CustomerState, policy: F2100PolicyDetailsDto, holdings: HoldingsDto) => {
	if (customer.age < 18) {
		return false;
	}
	if (policy.utbetaling.utbetalingStatus || holdings.currentMarketValue === 0 || policy.erOppsigelseBestilt) {
		return false;
	}

	return (
		policy.konfig.flexibleUtbetalingEnabled ||
		policy.konfig.livrenteUtbetalingEnabled ||
		(canOrderUtbetaling(customer.dateOfBirth, policy, holdings) && policy.konfig.utbetalingEnabled)
	);
};

const months = [
	{ value: 1, label: "Januar" },
	{ value: 2, label: "Februar" },
	{ value: 3, label: "Mars" },
	{ value: 4, label: "April" },
	{ value: 5, label: "Mai" },
	{ value: 6, label: "Juni" },
	{ value: 7, label: "Juli" },
	{ value: 8, label: "August" },
	{ value: 9, label: "September" },
	{ value: 10, label: "Oktober" },
	{ value: 11, label: "November" },
	{ value: 12, label: "Desember" },
] as const;
export type Maned = (typeof months)[number];

export const findPossibleUtbetalingMonths = (dateOfBirth?: number | Date, ageForUtbetaling?: number, orderCutOffDate?: number) => {
	/* Up to the cutoff date, the current month should be included */
	const monthOffset = orderCutOffDate !== undefined && new Date().getDate() <= orderCutOffDate ? 0 : 1;
	const currentMonth = new Date().getMonth();

	const visibleMonths = months
		.slice(currentMonth + monthOffset)
		.concat(months)
		.slice(0, 7);

	if (ageForUtbetaling && dateOfBirth) {
		return visibleMonths.filter((month) => {
			let firstOfMonth = setMonth(setDate(new Date(), 1), month.value - 1);
			if (firstOfMonth.getMonth() < currentMonth) {
				firstOfMonth = addYears(firstOfMonth, 1);
			}
			return differenceInYears(firstOfMonth, setDate(dateOfBirth, 1)) >= ageForUtbetaling;
		});
	}
	return visibleMonths;
};

export const getPrognosisAge = (policy: F2100PolicyDetailsDto | undefined, dateOfBirth?: number | Date, selectedMonth?: Maned | null, current?: Date) => {
	if (!policy || !policy.konfig) {
		return undefined;
	}

	let lowestAllowedPrognosisAge: number;
	if (policy.konfig.flexibleUtbetalingEnabled || policy.konfig.livrenteUtbetalingEnabled) {
		lowestAllowedPrognosisAge = policy.forsikretAlder ?? 0;
	} else {
		lowestAllowedPrognosisAge = Math.max(policy.forsikretAlder ?? 0, policy.epkDetaljer?.blEpk?.lavesteUtbetalingsalder || policy.konfig.ageForUtbetaling);
	}
	if (selectedMonth && dateOfBirth) {
		const currentDate = current ?? new Date(); // 'current' brukes av tester
		const birthDate = new Date(dateOfBirth);

		const selectedMonthIndex = selectedMonth.value - 1;
		const paymentYear = selectedMonthIndex >= currentDate.getMonth() ? currentDate.getFullYear() : currentDate.getFullYear() + 1;
		const paymentStartDate = new Date(paymentYear, selectedMonthIndex, 1);

		const birthdayThisYear = new Date(currentDate.getFullYear(), birthDate.getMonth(), birthDate.getDate());
		const nextBirthday = birthdayThisYear >= currentDate ? birthdayThisYear : new Date(currentDate.getFullYear() + 1, birthDate.getMonth(), birthDate.getDate());

		if (!(policy.konfig.flexibleUtbetalingEnabled || policy.konfig.livrenteUtbetalingEnabled)) {
			if (nextBirthday.getTime() <= paymentStartDate.getTime()) {
				lowestAllowedPrognosisAge += 1;
			}
		}
	}

	return lowestAllowedPrognosisAge;
};

export const getLivrenteUtbetalingDuration = (policy) => {
	const policyAge = differenceInYears(Date.now(), policy.innmeldtDate);
	return policyAge >= 6 ? 6 : 12 - policyAge;
};

export const onlyOneTimeAllowed = (config: Config, policy: F2100PolicyDetailsDto, holdings: HoldingsDto) => {
	if (!config || !policy || !holdings) {
		return true;
	}

	const {
		konfig: { percentageOfGForEngangsutbetaling },
	} = policy;
	const { currentMarketValue } = holdings;
	const { gVerdi } = config;

	return currentMarketValue < (percentageOfGForEngangsutbetaling / 100) * gVerdi;
};

export const getUtbetalingAccountNumber = (policy: F2100PolicyDetailsDto | undefined, customer: CustomerState | undefined) => {
	if (!policy || !customer) {
		return null;
	}
	const utbetales = [UNDER_UTBETALING, AKTIV_UNDER_UTBETALING].includes(policy.utbetaling.utbetalingStatus ?? "");
	if (utbetales) {
		return customer.f2100Account?.accountNumber;
	}
	return policy.utbetaling.utbetalingKontonummer;
};
